import React, { useContext, useEffect, useMemo } from "react";
import { useTheme } from "@mui/material";
import { ConfiguratorContext } from "../../../../../../contexts/ConfiguratorContext";
import DocumentDefinitionsColumn, {
  getOptionKey,
} from "../../../../shared/DocumentDefinitionsColumn";
import { FlexContainer } from "../../../../../../shared/components/styled/Container.styled";
import { DOCUMENTS_ALLOWED_TO_BE_UPLOADED_KEY } from "../../../../../../shared/constants/Configurator.constants";
import { useIsMount } from "../../../../../../shared/hooks/useIsMount";
import { serializeDocumentUploadSelection } from "../../../../../../shared/utils/documentUploadSelections";
import { hex2rgba } from "../../../../../../shared/utils/utility-functions";

/**
 * The document definitions a borrower may upload, picked the way the viewable documents are
 * picked. Naming none of them is a valid choice; the borrower is then offered nothing to upload.
 */
const DocumentUploadDefinitionsSetting = ({
  options = [],
  loading = false,
  selection = [],
  setSelection = () => {},
}) => {
  const theme = useTheme();
  const isFirstRender = useIsMount();
  const { addNewEdittedField } = useContext(ConfiguratorContext);

  // A client configured before the categories came from XDoc saved names that are no definitions
  // and so cannot be ticked. They are left out of the ticks rather than written out of the
  // configuration: their borrowers go on being offered them, and publishing anything else on
  // this page cannot quietly take the upload away. Editing this section replaces them, which is
  // the admin saying what they want (US-1027823 product item 4).
  const ticked = useMemo(() => {
    if (options.length === 0) {
      return selection;
    }

    const definitions = options.map(getOptionKey);
    return selection.filter((key) => definitions.includes(key));
  }, [options, selection]);

  useEffect(() => {
    if (!isFirstRender) {
      addNewEdittedField({
        key: DOCUMENTS_ALLOWED_TO_BE_UPLOADED_KEY,
        value: serializeDocumentUploadSelection(selection, options),
        modified: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selection]);

  return (
    <FlexContainer
      flexdirection="column"
      alignitems="stretch"
      justifycontent="flex-start"
      gap="8px"
      width="100%"
      height="100%"
      padding="25px"
      background={hex2rgba(theme.palette.primary.main, 0.1)}
      sx={{ boxSizing: "border-box" }}
    >
      <DocumentDefinitionsColumn
        columnTitle="DOCUMENTS"
        options={options}
        selection={ticked}
        onSelectionChange={setSelection}
        loading={loading}
      />
    </FlexContainer>
  );
};

export default DocumentUploadDefinitionsSetting;
